import { Component, OnInit, Inject, LOCALE_ID, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule, DatePipe, registerLocaleData } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, NativeDateAdapter, DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { MatTableDataSource } from '@angular/material/table';
import { Sort } from '@angular/material/sort';
import localeEs from '@angular/common/locales/es';

registerLocaleData(localeEs);

export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'DD/MM/YYYY',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

// Adaptador personalizado para fechas
export class CustomDateAdapter extends NativeDateAdapter {
  override getFirstDayOfWeek(): number {
    return 1; // Lunes como primer día de la semana
  }

  override format(date: Date, displayFormat: any): string {
    if (displayFormat === 'DD/MM/YYYY') {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }
    return super.format(date, displayFormat);
  }
}

interface UpdateType {
  id: number;
  name: string;
}

interface UpdateArea {
  id: number;
  name: string;
}

interface SearchResult {
  area: string;
  tipo: string;
  fecha: string;
  hora: string;
  detalle: string;
}

interface SearchResponse {
  message: string;
  data: SearchResult[];
}

@Component({
  selector: 'app-search-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatTableModule,
    MatSortModule,
    MatIconModule,
    MatPaginatorModule,
    HttpClientModule
  ],
  providers: [
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_LOCALE, useValue: 'es-ES' },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    DatePipe
  ],
  templateUrl: './search-modal.component.html',
  styleUrls: ['./search-modal.component.scss']
})
export class SearchModalComponent implements OnInit, AfterViewInit {
  showFilters = false;
  searchText = '';
  selectedArea = 0;
  selectedType = 0;
  fromDate: Date | null = null;
  toDate: Date | null = null;
  showDatePickers = false;

  updateTypes: UpdateType[] = [];
  updateAreas: UpdateArea[] = [];
  searchResults: SearchResult[] = [];
  dataSource = new MatTableDataSource<SearchResult>([]);
  
  displayedColumns: string[] = ['area', 'tipo', 'fecha', 'detalle'];
  
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  
  constructor(
    public dialogRef: MatDialogRef<SearchModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private http: HttpClient,
    private datePipe: DatePipe,
    private cdr: ChangeDetectorRef
  ) {
    // Establecer fechas por defecto (primer y último día del mes actual)
    const now = new Date();
    this.fromDate = new Date(now.getFullYear(), now.getMonth(), 1);
    this.toDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  }

  ngOnInit(): void {
    this.loadUpdateTypes();
    this.loadUpdateAreas();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    // Force change detection after view init
    setTimeout(() => {
      if (this.dataSource.data.length > 0) {
        this.dataSource.paginator = this.paginator;
      }
    });
  }

  loadUpdateTypes(): void {
    this.http.get<UpdateType[]>('https://cde-oxy.g-talent.cl/api/UpdateType')
      .subscribe({
        next: (types: UpdateType[]) => {
          this.updateTypes = types;
        },
        error: (error: any) => {
          console.error('Error loading update types:', error);
        }
      });
  }

  loadUpdateAreas(): void {
    this.http.get<UpdateArea[]>('https://cde-oxy.g-talent.cl/api/UpdateArea')
      .subscribe({
        next: (areas: UpdateArea[]) => {
          this.updateAreas = areas;
        },
        error: (error: any) => {
          console.error('Error loading update areas:', error);
        }
      });
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  toggleDatePickers(): void {
    this.showDatePickers = !this.showDatePickers;
  }

  onSearch(): void {
    const textParam = this.searchText.trim() || 'null';
    const fromParam = this.fromDate ? this.formatDate(this.fromDate) : this.formatDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    const toParam = this.toDate ? this.formatDate(this.toDate) : this.formatDate(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0));

    const url = `https://cde-oxy.g-talent.cl/api/UpdateValueSearch/area/${this.selectedArea}/type/${this.selectedType}/text/${textParam}/from/${fromParam}/to/${toParam}`;

    console.log('Searching with URL:', url);

    this.http.get<SearchResponse>(url).subscribe({
      next: (response: SearchResponse) => {
        console.log('Search response:', response);
        this.searchResults = response.data || [];
        this.dataSource.data = this.searchResults;
        
        // Ensure paginator is connected after data is loaded
        setTimeout(() => {
          if (this.paginator) {
            this.dataSource.paginator = this.paginator;
            this.paginator.firstPage();
            this.cdr.detectChanges();
          }
        });
      },
      error: (error: any) => {
        console.error('Error searching:', error);
        this.searchResults = [];
        this.dataSource.data = [];
      }
    });
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatDateForDisplay(date: Date): string {
    return this.datePipe.transform(date, 'dd/MM/yyyy') || '';
  }

  // Getter para mostrar las fechas en formato DD/MM/YYYY
  get fromDateDisplay(): string {
    return this.fromDate ? this.formatDateForDisplay(this.fromDate) : '';
  }

  get toDateDisplay(): string {
    return this.toDate ? this.formatDateForDisplay(this.toDate) : '';
  }

  sortData(sort: Sort): void {
    const data = this.searchResults.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'area':
          return this.compare(a.area, b.area, isAsc);
        case 'tipo':
          return this.compare(a.tipo, b.tipo, isAsc);
        case 'fecha':
          return this.compare(new Date(a.fecha), new Date(b.fecha), isAsc);
        case 'hora':
          return this.compare(a.hora, b.hora, isAsc);
        case 'detalle':
          return this.compare(a.detalle, b.detalle, isAsc);
        default:
          return 0;
      }
    });
  }

  private compare(a: any, b: any, isAsc: boolean): number {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  // Método para formatear la hora a HH:mm:ss
  formatTime(timeString: string): string {
    if (!timeString) return '';
    
    // Si ya es un string con formato de hora, extraer solo HH:mm:ss
    if (timeString.includes(':')) {
      const parts = timeString.split(':');
      if (parts.length >= 3) {
        // Asegurar que cada parte tenga 2 dígitos
        const hours = parts[0].padStart(2, '0');
        const minutes = parts[1].padStart(2, '0');
        const seconds = parts[2].split('.')[0].padStart(2, '0'); // Remover decimales si existen
        return `${hours}:${minutes}:${seconds}`;
      }
    }
    
    // Si es un timestamp o fecha, convertir a hora
    try {
      const date = new Date(timeString);
      if (!isNaN(date.getTime())) {
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const seconds = date.getSeconds().toString().padStart(2, '0');
        return `${hours}:${minutes}:${seconds}`;
      }
    } catch (error) {
      console.warn('Error formatting time:', error);
    }
    
    return timeString;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  // Método para abrir datepicker programáticamente si es necesario
  openDatePicker(picker: any): void {
    picker.open();
  }
}
